import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, ChevronRight, Inbox, Users, User } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/StatusDisplay";
import { useT } from "@/lib/i18n";

const fmt = (n) => `$${Math.abs(n).toFixed(2)}`;
const money = (n) => `${n < 0 ? "-" : ""}$${Math.abs(n).toFixed(2)}`;
const ALL = "__all__";

const formatDateTime = (iso) => {
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }),
    time: d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }),
  };
};

export default function History() {
  const [counts, setCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(ALL);
  const [authorFilter, setAuthorFilter] = useState(ALL);
  const navigate = useNavigate();
  const t = useT();

  useEffect(() => {
    api
      .listCounts()
      .then(setCounts)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const employeeNames = useMemo(
    () => [...new Set(counts.map((c) => c.employee_name))].sort((a, b) => a.localeCompare(b)),
    [counts]
  );

  const authorNames = useMemo(
    () => [...new Set(counts.map((c) => c.created_by_name).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [counts]
  );

  const visible = counts.filter(
    (c) =>
      (filter === ALL || c.employee_name === filter) &&
      (authorFilter === ALL || c.created_by_name === authorFilter)
  );

  const groups = useMemo(() => {
    const map = new Map();
    visible.forEach((c) => {
      const key = new Date(c.created_at).toLocaleDateString(undefined, {
        weekday: "short", month: "short", day: "numeric", year: "numeric",
      });
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    });
    return [...map.entries()].map(([date, items]) => ({
      date,
      items,
      totalDeposit: items.reduce((s, c) => s + c.cash_to_deposit, 0),
      netCents: items.reduce((s, c) => s + Math.round(c.difference * 100), 0),
    }));
  }, [visible]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-foreground">
            {t("nav_history")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("history_subtitle")}</p>
        </div>
        {counts.length > 0 && (
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger
                data-testid="history-employee-filter"
                className="h-11 w-full min-w-[180px] sm:w-48"
              >
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <SelectValue />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL} data-testid="history-filter-all">
                  {t("all_employees")}
                </SelectItem>
                {employeeNames.map((name) => (
                  <SelectItem key={name} value={name} data-testid={`history-filter-${name}`}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={authorFilter} onValueChange={setAuthorFilter}>
              <SelectTrigger
                data-testid="history-author-filter"
                className="h-11 w-full min-w-[180px] sm:w-48"
              >
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <SelectValue />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL} data-testid="history-author-filter-all">
                  {t("counted_by_anyone")}
                </SelectItem>
                {authorNames.map((name) => (
                  <SelectItem key={name} value={name} data-testid={`history-author-filter-${name}`}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {loading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-24 w-full rounded-2xl" />
          ))}
        </div>
      ) : counts.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 rounded-2xl border-dashed border-border p-12 text-center">
          <Inbox className="h-10 w-10 text-muted-foreground" />
          <p className="font-display text-lg font-bold text-foreground">{t("no_counts_yet")}</p>
          <p className="text-sm text-muted-foreground">{t("saved_counts_appear")}</p>
        </Card>
      ) : visible.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 rounded-2xl border-dashed border-border p-12 text-center">
          <Inbox className="h-10 w-10 text-muted-foreground" />
          <p className="font-display text-lg font-bold text-foreground">{t("no_matching_counts")}</p>
          <p className="text-sm text-muted-foreground">{t("try_different_filters")}</p>
        </Card>
      ) : (
        <div className="space-y-6" data-testid="history-list">
          {groups.map((g) => {
            const netLabel = g.netCents === 0
              ? t("status_balanced")
              : `${g.netCents < 0 ? t("status_short") : t("status_over")} $${(Math.abs(g.netCents) / 100).toFixed(2)}`;
            const netColor = g.netCents === 0
              ? "text-emerald-600"
              : g.netCents < 0 ? "text-rose-600" : "text-amber-600";
            return (
              <div key={g.date} className="space-y-3">
                <div
                  data-testid="shift-summary"
                  className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-secondary px-4 py-3"
                >
                  <span className="font-display text-sm font-bold text-foreground">{g.date}</span>
                  <div className="flex items-center gap-5 text-xs font-semibold">
                    <span className="text-muted-foreground">
                      {t("deposits")}{" "}
                      <span className="tabular-nums text-foreground" data-testid="shift-total-deposit">
                        {money(g.totalDeposit)}
                      </span>
                    </span>
                    <span className="text-muted-foreground">
                      {t("net")}{" "}
                      <span className={`tabular-nums ${netColor}`} data-testid="shift-net">{netLabel}</span>
                    </span>
                  </div>
                </div>
                {g.items.map((c) => {
                  const { date, time } = formatDateTime(c.created_at);
                  return (
                    <Card
                      key={c.id}
                      onClick={() => navigate(`/history/${c.id}`)}
                      data-testid={`history-item-${c.id}`}
                      className="flex cursor-pointer items-center justify-between rounded-2xl border-border p-5 transition-colors duration-200 hover:bg-secondary active:scale-[0.99]"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-display text-lg font-bold text-foreground truncate">
                            {c.employee_name}
                          </span>
                          <StatusBadge status={c.status} />
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1.5">
                            <Calendar className="h-3.5 w-3.5" />
                            {date} · {time}
                          </span>
                          {c.created_by_name && (
                            <span className="flex items-center gap-1.5" data-testid={`history-author-${c.id}`}>
                              <User className="h-3.5 w-3.5" />
                              {c.created_by_name}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-display text-base font-bold tabular-nums text-foreground">
                            {c.status === "BALANCED" ? "$0.00" : fmt(c.difference)}
                          </p>
                          <p className="text-xs text-muted-foreground tabular-nums">
                            {t("deposit_word")} {money(c.cash_to_deposit)}
                          </p>
                        </div>
                        <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
                      </div>
                    </Card>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
